import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      main: '#004d34', // Rafi project primary green
      light: '#e5f3fb', // Rafi project light green
      dark: '#004d34', // Rafi project dark green
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#707a82', // Rafi project secondary gray
      light: '#e7ecf0', // Rafi project light gray
      dark: '#707a82', // Rafi project dark gray
      contrastText: '#ffffff',
    },
    success: {
      main: '#4bd08b', // Rafi project success green
      light: '#dffff3', // Rafi project light success
      dark: '#4bd08b', // Rafi project dark success
      contrastText: '#ffffff',
    },
    info: {
      main: '#46caeb', // Rafi project info blue
      light: '#e1f5fa', // Rafi project light info
      dark: '#46caeb', // Rafi project dark info
      contrastText: '#ffffff',
    },
    error: {
      main: '#fb977d', // Rafi project error orange
      light: '#ffede9', // Rafi project light error
      dark: '#fb977d', // Rafi project dark error
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f8c076', // Rafi project warning yellow
      light: '#fff6ea', // Rafi project light warning
      dark: '#f8c076', // Rafi project dark warning
      contrastText: '#ffffff',
    },
    purple: {
      A50: '#f1ebff', // Rafi project purple variants
      A100: '#8763da',
      A200: '#557fb9',
    },
    grey: {
      100: '#F2F6FA', // Rafi project grey variants
      200: '#f0f5f9',
      300: '#DFE5EF',
      400: '#7C8FAC',
      500: '#5A6A85',
      600: '#111c2d',
    },
    text: {
      primary: '#111c2d', // Rafi project text colors
      secondary: '#111c2d',
    },
    action: {
      disabledBackground: 'rgba(73,82,88,0.12)', // Rafi project action colors
      hoverOpacity: 0.02,
      hover: '#f6f9fc',
    },
    divider: '#e5eaef', // Rafi project divider color
    background: {
      default: '#F0F5F9', // Rafi project background colors
      dark: '#F0F5F9',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif', // Rafi project uses Yekan font
    h1: {
      fontWeight: 600,
      fontSize: '2.25rem',
      lineHeight: '2.75rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: '2.25rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: '1.75rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.3125rem',
      lineHeight: '1.6rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: '1.6rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: '1.2rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    button: {
      textTransform: 'capitalize', // Rafi project button style
      fontWeight: 400,
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: '1.334rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    body2: {
      fontSize: '0.75rem',
      letterSpacing: '0rem',
      fontWeight: 400,
      lineHeight: '1rem',
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    caption: {
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
    overline: {
      fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
            padding: '12px 14px', // Rafi project input padding
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '7px', // Rafi project border radius
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#004d34', // Rafi project primary color
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#004d34', // Rafi project primary color
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
          fontWeight: 400, // Rafi project button weight
          borderRadius: '25px', // Rafi project button radius
          textTransform: 'none', // Rafi project button transform
          boxShadow: 'none', // Rafi project button shadow
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 77, 52, 0.3)', // Rafi project hover shadow
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)', // Rafi project primary gradient
          '&:hover': {
            background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)', // Rafi project hover gradient
          },
        },
        textPrimary: {
          backgroundColor: '#e5f3fb', // Rafi project primary light
          '&:hover': {
            backgroundColor: '#004d34', // Rafi project primary main
            color: 'white',
          },
        },
        textSecondary: {
          backgroundColor: '#e7ecf0', // Rafi project secondary light
          '&:hover': {
            backgroundColor: '#707a82', // Rafi project secondary main
            color: 'white',
          },
        },
        textSuccess: {
          backgroundColor: '#dffff3', // Rafi project success light
          '&:hover': {
            backgroundColor: '#4bd08b', // Rafi project success main
            color: 'white',
          },
        },
        textError: {
          backgroundColor: '#ffede9', // Rafi project error light
          '&:hover': {
            backgroundColor: '#fb977d', // Rafi project error main
            color: 'white',
          },
        },
        textInfo: {
          backgroundColor: '#e1f5fa', // Rafi project info light
          '&:hover': {
            backgroundColor: '#46caeb', // Rafi project info main
            color: 'white',
          },
        },
        textWarning: {
          backgroundColor: '#fff6ea', // Rafi project warning light
          '&:hover': {
            backgroundColor: '#f8c076', // Rafi project warning main
            color: 'white',
          },
        },
        outlinedPrimary: {
          '&:hover': {
            backgroundColor: '#004d34', // Rafi project primary main
            color: 'white',
          },
        },
        outlinedSecondary: {
          '&:hover': {
            backgroundColor: '#707a82', // Rafi project secondary main
            color: 'white',
          },
        },
        outlinedError: {
          '&:hover': {
            backgroundColor: '#fb977d', // Rafi project error main
            color: 'white',
          },
        },
        outlinedSuccess: {
          '&:hover': {
            backgroundColor: '#4bd08b', // Rafi project success main
            color: 'white',
          },
        },
        outlinedInfo: {
          '&:hover': {
            backgroundColor: '#46caeb', // Rafi project info main
            color: 'white',
          },
        },
        outlinedWarning: {
          '&:hover': {
            backgroundColor: '#f8c076', // Rafi project warning main
            color: 'white',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          width: "100%",
          padding: "30px", // Rafi project card padding
          backgroundImage: "none", // Rafi project card background
          borderRadius: "18px", // Rafi project card radius
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '30px', // Rafi project card content padding
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 24px', // Rafi project card header padding
        },
        title: {
          fontSize: '1.125rem', // Rafi project card header title size
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)', // Rafi project primary gradient
          boxShadow: '0 8px 25px rgba(0, 77, 52, 0.4)', // Rafi project fab shadow
          '&:hover': {
            background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)', // Rafi project fab hover
            boxShadow: '0 12px 35px rgba(0, 77, 52, 0.5)', // Rafi project fab hover shadow
            transform: 'translateY(-2px)',
          },
        },
        sizeSmall: {
          width: 30, // Rafi project small fab size
          height: 30,
          minHeight: 30,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600, // Rafi project chip weight
          fontSize: '0.75rem', // Rafi project chip size
          borderRadius: '20px', // Rafi project chip radius
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F2F6FA', // Rafi project table head background
            fontWeight: 'bold',
            color: '#111c2d', // Rafi project text color
            borderBottom: '1px solid #e5eaef', // Rafi project divider color
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F2F6FA', // Rafi project table row hover
          },
          '&:nth-of-type(even)': {
            backgroundColor: '#fafbfc',
          },
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e5eaef', // Rafi project divider color
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
          fontWeight: 'bold',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiStepContent: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiSpeedDial: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiSpeedDialAction: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTimeline: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTimelineSeparator: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTimelineContent: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTimelineOppositeContent: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTreeView: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiTreeItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
    MuiAlertDescription: {
      styleOverrides: {
        root: {
          fontFamily: 'Yekan, Vazir, Tahoma, Arial, sans-serif',
        },
      },
    },
  },
});

export default theme;